import { z } from 'zod';
import type { TFunction } from 'i18next';
import type { IModuleField } from '@/features/module-builder/types';
import { evaluateDependencies } from './dependencies';

/**
 * Runtime zod schema for dynamic record forms. Values are keyed by field NAME.
 *
 * All validation lives in a single `superRefine` so hidden (dependency) fields
 * are skipped entirely and required-ness stays conditional — mirroring the
 * backend RecordValidationService pipeline.
 */

export type RecordFormValues = Record<string, unknown>;

export const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_REGEX = /^\+?[0-9\s\-()]+$/;

export function isEmptyFormValue(value: unknown): boolean {
    if (value === null || value === undefined || value === '') return true;
    if (Array.isArray(value)) return value.length === 0;
    return false;
}

/** Fields the user can actually edit (drives defaults, serialization, validation). */
export function isWritableField(field: IModuleField): boolean {
    return (
        field.isActive &&
        !field.isReadOnly &&
        field.fieldType !== 'AUTO_INCREMENT' &&
        field.fieldSource !== 'SYSTEM'
    );
}

function asNumber(raw: unknown): number | undefined {
    if (raw === undefined || raw === null || raw === '') return undefined;
    const num = Number(raw);
    return Number.isFinite(num) ? num : undefined;
}

function asTime(raw: unknown): number | undefined {
    if (raw === undefined || raw === null || raw === '') return undefined;
    if (typeof raw !== 'string' && typeof raw !== 'number') return undefined;
    const time = new Date(raw).getTime();
    return Number.isNaN(time) ? undefined : time;
}

function optionValues(field: IModuleField): Set<string> {
    return new Set((field.options ?? []).map((o) => o.value));
}

/**
 * Validate a single non-empty form value for its field. Returns a translated
 * error message, or `null` when valid. Mirrors the backend per-type table.
 */
export function validateFieldValue(
    field: IModuleField,
    value: unknown,
    t: TFunction,
): string | null {
    const rules = field.validationRules ?? {};
    const label = field.displayName;

    switch (field.fieldType) {
        case 'TEXT':
        case 'TEXTAREA': {
            const text = String(value);
            if (rules.minLength !== undefined && text.length < rules.minLength) {
                return t('recordForm.validation.minLength', {
                    defaultValue: '{{field}} must be at least {{count}} characters',
                    field: label,
                    count: rules.minLength,
                });
            }
            if (rules.maxLength !== undefined && text.length > rules.maxLength) {
                return t('recordForm.validation.maxLength', {
                    defaultValue: '{{field}} must be at most {{count}} characters',
                    field: label,
                    count: rules.maxLength,
                });
            }
            if (rules.pattern) {
                try {
                    if (!new RegExp(rules.pattern).test(text)) {
                        return t('recordForm.validation.pattern', {
                            defaultValue: '{{field}} does not match the required format',
                            field: label,
                        });
                    }
                } catch {
                    return null;
                }
            }
            return null;
        }
        case 'EMAIL': {
            const text = String(value);
            if (!EMAIL_REGEX.test(text) || text.length > 255) {
                return t('recordForm.validation.email', {
                    defaultValue: '{{field}} must be a valid email address',
                    field: label,
                });
            }
            return null;
        }
        case 'URL': {
            try {
                const url = new URL(String(value));
                if (url.protocol !== 'http:' && url.protocol !== 'https:') throw new Error();
                return null;
            } catch {
                return t('recordForm.validation.url', {
                    defaultValue: '{{field}} must be a valid http(s) URL',
                    field: label,
                });
            }
        }
        case 'PHONE': {
            const text = String(value);
            if (!PHONE_REGEX.test(text) || text.length > 20) {
                return t('recordForm.validation.phone', {
                    defaultValue: '{{field}} must be a valid phone number',
                    field: label,
                });
            }
            return null;
        }
        case 'NUMBER':
        case 'CURRENCY':
        case 'PERCENTAGE': {
            const num = asNumber(value);
            if (num === undefined) {
                return t('recordForm.validation.number', {
                    defaultValue: '{{field}} must be a number',
                    field: label,
                });
            }
            const min = asNumber(rules.min) ?? (field.fieldType === 'PERCENTAGE' ? 0 : undefined);
            const max = asNumber(rules.max) ?? (field.fieldType === 'PERCENTAGE' ? 100 : undefined);
            if (min !== undefined && num < min) {
                return t('recordForm.validation.min', {
                    defaultValue: '{{field}} must be at least {{min}}',
                    field: label,
                    min,
                });
            }
            if (max !== undefined && num > max) {
                return t('recordForm.validation.max', {
                    defaultValue: '{{field}} must be at most {{max}}',
                    field: label,
                    max,
                });
            }
            return null;
        }
        case 'RATING': {
            const scale = field.settings?.scale ?? 5;
            const num = asNumber(value);
            if (num === undefined || !Number.isInteger(num) || num < 0 || num > scale) {
                return t('recordForm.validation.rating', {
                    defaultValue: '{{field}} must be between 0 and {{max}}',
                    field: label,
                    max: scale,
                });
            }
            return null;
        }
        case 'DATE':
        case 'DATETIME': {
            const time = asTime(value);
            if (time === undefined) {
                return t('recordForm.validation.date', {
                    defaultValue: '{{field}} must be a valid date',
                    field: label,
                });
            }
            const min = asTime(rules.min);
            const max = asTime(rules.max);
            if (min !== undefined && time < min) {
                return t('recordForm.validation.dateMin', {
                    defaultValue: '{{field}} is before the earliest allowed date',
                    field: label,
                });
            }
            if (max !== undefined && time > max) {
                return t('recordForm.validation.dateMax', {
                    defaultValue: '{{field}} is after the latest allowed date',
                    field: label,
                });
            }
            return null;
        }
        case 'SELECT': {
            if (!optionValues(field).has(String(value))) {
                return t('recordForm.validation.option', {
                    defaultValue: '{{field}} must be one of the configured options',
                    field: label,
                });
            }
            return null;
        }
        case 'MULTI_SELECT': {
            const items = Array.isArray(value) ? value : [];
            const allowed = optionValues(field);
            if (items.some((item) => !allowed.has(String(item)))) {
                return t('recordForm.validation.option', {
                    defaultValue: '{{field}} must be one of the configured options',
                    field: label,
                });
            }
            const min = asNumber(rules.min);
            const max = asNumber(rules.max);
            if (min !== undefined && items.length < min) {
                return t('recordForm.validation.minItems', {
                    defaultValue: 'Select at least {{count}} options for {{field}}',
                    field: label,
                    count: min,
                });
            }
            if (max !== undefined && items.length > max) {
                return t('recordForm.validation.maxItems', {
                    defaultValue: 'Select at most {{count}} options for {{field}}',
                    field: label,
                    count: max,
                });
            }
            return null;
        }
        case 'BOOLEAN': {
            return typeof value === 'boolean'
                ? null
                : t('recordForm.validation.boolean', {
                      defaultValue: '{{field}} must be true or false',
                      field: label,
                  });
        }
        // TIMEZONE / LOOKUP / USER values come from constrained selects.
        default:
            return null;
    }
}

/**
 * Build the record form schema for a set of fields. `namePrefix`-agnostic:
 * validate against the record sub-object (callers with a `custom.` prefix pass
 * that slice).
 */
export function buildRecordSchema(
    fields: IModuleField[],
    t: TFunction,
): z.ZodType<RecordFormValues, RecordFormValues> {
    const writable = fields.filter(isWritableField);
    return z.record(z.string(), z.unknown()).superRefine((values, ctx) => {
        const { hidden, required } = evaluateDependencies(writable, values);
        for (const field of writable) {
            if (hidden.has(field.name)) continue;
            const value = values[field.name];
            if (isEmptyFormValue(value)) {
                if (required.has(field.name)) {
                    ctx.addIssue({
                        code: 'custom',
                        path: [field.name],
                        message: t('recordForm.validation.required', {
                            defaultValue: '{{field}} is required',
                            field: field.displayName,
                        }),
                    });
                }
                continue;
            }
            const error = validateFieldValue(field, value, t);
            if (error) {
                ctx.addIssue({ code: 'custom', path: [field.name], message: error });
            }
        }
    });
}
